'use strict';

const _ = require('lodash');
const {
    clean,
    draftify,
    compileProperties,
    compileRows
} = require('../compiler');

const PROPERTIES = {
    PAGE: ['title'],
    ITEM: [
        'answer',
        'domain',
        'goal',
        'hidden',
        'label',
        'template',
        'type',
        'unfavorable'
    ],
    ISSUE: ['createdAt', 'description', 'urgent', 'goal', 'domain'],
    ACTION: ['createdAt', 'description', 'dueDate', 'urgent', 'statusChanged']
};

const DRAFT_PATHS = [
    'items.*',
    'items.*.issues.*',
    'items.*.issues.*.comment.*',
    'items.*.issues.*.actions.*',
    'items.*.issues.*.actions.*.comment.*'
];

const OMIT = ['draft', 'diff'];

const TYPES = {
    DRAFT: ['header', 'label'],
    KEEP: ['plain', 'static', 'dynamic', 'other']
};

const INACTIVE = ['completed', 'cancelled'];

const describeRelation = ({ relation, lists }) => {
    if (Object.prototype.hasOwnProperty.call(relation, 'value')) {
        return relation.value || '';
    }
    const list = lists?.[relation.key] || [];
    return list.find((l) => l.id === relation.id)?.name || 'undefined';
};

const describe = ({ template, relations, lists }) => {
    if (template) {
        return (relations || []).reduce(
            (acc, relation) =>
                acc.replace(
                    `%%${relation.key}%%`,
                    describeRelation({ relation, lists })
                ),
            template
        );
    }
    return 'Unknown';
};

const cancelAction = ({ action, lists }) => {
    const active = !action.details?.find(
        (d) => d.key === 'status' && INACTIVE.includes(d.id)
    );
    if (active) {
        const cancelled = _.cloneDeep(action);
        const previous = _.has(cancelled, 'diff.status')
            ? cancelled.diff.status.previous
            : _.omit(
                  cancelled.details.find((d) => d.key === 'status'),
                  ['key']
              );
        const current = lists.status.find((s) => s.id === 'cancelled');
        const details = cancelled.details.map((d) => {
            if (d.key === 'status') {
                return _.assign({ key: 'status' }, current);
            }
            return d;
        });
        const diff = _.assign(cancelled.diff || {}, {
            status: { previous, current }
        });
        return _.assign(cancelled, {
            details,
            diff,
            cancel: true
        });
    }
    return action;
};

const cancelIssue = ({ issue, lists }) => {
    const cancelled = _.cloneDeep(issue);
    const previous = _.has(cancelled, 'diff.status')
        ? cancelled.diff.status.previous
        : _.omit(
              cancelled.details?.find((d) => d.key === 'status'),
              ['key']
          );
    const current = lists.status?.find((s) => s.id === 'cancelled');
    const details = cancelled.details?.map((d) => {
        if (d.key === 'status') {
            return _.assign({ key: 'status' }, current);
        }
        return d;
    });
    const diff = _.assign(cancelled.diff || {}, {
        status: { previous, current }
    });
    const actions = cancelled.actions?.map((action) =>
        cancelAction({
            action,
            lists
        })
    );
    return _.pickBy(
        _.assign(cancelled, {
            details,
            diff,
            actions,
            cancel: true
        }),
        _.identity
    );
};

const updateIssue = ({ issue, lists, template }) => {
    const updated = _.cloneDeep(issue);
    const relations = updated.relations.map((r) => {
        const { key, id } = r;
        if (id) {
            const list = lists[key] || [];
            const name = list.find((l) => l.id === id)?.name || r.name;
            return { key, id, name };
        }
        return r;
    });
    const previous = _.has(updated, 'diff.description')
        ? updated.diff.description.previous
        : updated.description;
    const current = describe({
        template,
        relations,
        lists
    });
    const diff = _.assign(updated.diff || {}, {
        description: {
            previous,
            current
        }
    });
    return _.assign(updated, {
        description: current,
        relations,
        diff
    });
};

const compileLists = (
    { currentPage, proposedPage },
    canReadClinicalData,
    clinicalUsers
) => {
    if (currentPage.lists) {
        const cancel = {};
        const update = {};
        const lists = _.cloneDeep(currentPage.lists);

        proposedPage.items?.forEach((item) => {
            item?.relations?.forEach((relation) => {
                const key = relation.key;
                if (!VALUABLE.includes(key)) {
                    const list = lists[key] || [];
                    relation?.values?.forEach((value) => {
                        const { id, name } = value;
                        if (!list.find((m) => m.id === id)) {
                            list.push({ id, name, unavailable: true });
                        }
                    });
                    lists[key] = list;
                }
            });
            item?.issues?.forEach((issue) => {
                const action = {};
                issue?.relations?.forEach((relation) => {
                    const { key, id, name } = relation;
                    if (!VALUABLE.includes(key)) {
                        const list = lists[key] || [];
                        const found = list.find((m) => m.id === id);
                        const active = !issue.details?.find(
                            (d) => d.key === 'status' && INACTIVE.includes(d.id)
                        );
                        if (!found) {
                            list.push({ id, name, unavailable: true });
                            if (active) {
                                action.cancel = true;
                            }
                        } else if (found.unavailable) {
                            if (active) {
                                action.cancel = true;
                            }
                        } else if (found.name !== name) {
                            if (active) {
                                action.update = true;
                            }
                        }
                        lists[key] = list;
                    }
                });
                issue?.details?.forEach((view) => {
                    const { key, id, name } = view;
                    const list = lists[key] || [];
                    if (!list.find((m) => m.id === id)) {
                        list.push({ id, name, unavailable: true });
                    }
                    lists[key] = list;
                });
                if (action.cancel) {
                    cancel[issue.id] = cancelIssue({ issue, lists });
                } else if (action.update) {
                    update[issue.id] = updateIssue({
                        issue,
                        lists,
                        template: item.template
                    });
                }
            });
        });
        const conflicts = _.pickBy(
            {
                cancel: _.values(cancel),
                update: _.values(update)
            },
            (obj) => !_.isEmpty(obj)
        );

        if (
            !canReadClinicalData &&
            lists.assignee &&
            Array.isArray(lists.assignee)
        ) {
            lists.assignee = lists.assignee.filter(
                (user) => !clinicalUsers.includes(user.id)
            );
        }

        return { lists, conflicts };
    }
    return {};
};

const VALUABLE = ['details', 'details_optional'];

const divide = (rows) =>
    rows.reduce((acc, row) => {
        const { key, value } = row;
        if (VALUABLE.includes(key)) {
            acc[key] = value;
        } else {
            const { key, id, name } = row;
            acc[key] = { id, name };
        }
        return acc;
    }, {});

const obtain = (divided) =>
    _.transform(
        divided,
        (acc, value, key) => {
            if (key === 'diff') {
                acc.diff = value;
            } else if (VALUABLE.includes(key)) {
                const obtained = acc.obtained || [];
                obtained.push({ key, value });
                acc.obtained = obtained;
            } else {
                const obtained = acc.obtained || [];
                if (value) {
                    const { id, name } = value;
                    obtained.push({ key, id, name });
                } else {
                    obtained.push({ key });
                }
                acc.obtained = obtained;
            }
            return acc;
        },
        {}
    );

const compileKeys = ({ current, proposed, initial, currentDiff }) => {
    if (current && proposed) {
        const before = _.assign(divide(current), { diff: currentDiff });
        const after = divide(proposed);
        const properties = _.difference(
            _.union(_.keys(before), _.keys(after)),
            ['diff']
        );
        const compiled = compileProperties({
            properties,
            before,
            after,
            initial
        });
        return obtain(compiled);
    } else {
        return { ...initial };
    }
};

const compileComment = ({ current, proposed }) => {
    if (current) {
        return { row: _.cloneDeep(current), draft: current.draft };
    }
    return {
        row: _.assign(_.cloneDeep(proposed), { draft: true }),
        draft: true
    };
};

const compileAction = ({ proposedAction, currentAction }) => {
    if (currentAction && !currentAction.draft) {
        const {
            details: currentDetails,
            comment: currentComment,
            diff: currentDiff
        } = _.cloneDeep(currentAction);
        const {
            id,
            details: proposedDetails,
            comment: proposedComment
        } = _.cloneDeep(proposedAction);
        const { obtained: details, diff } = compileKeys({
            current: currentDetails,
            proposed: proposedDetails,
            initial: {},
            currentDiff
        });
        const initial = {
            id,
            details,
            diff
        };
        const action = compileProperties({
            properties: PROPERTIES.ACTION,
            before: currentAction,
            after: proposedAction,
            initial
        });
        const { rows: comment, draft: draftComment } = compileRows({
            current: currentComment,
            proposed: proposedComment,
            compileRow: compileComment
        });
        action.comment = !_.isEmpty(comment) && comment;
        const compiled = {
            action,
            draft: !_.isEmpty(action.diff) || draftComment
        };
        return {
            action: _.pickBy(compiled.action, _.identity),
            draft: compiled.draft
        };
    } else {
        const action = _.pickBy(
            draftify({
                object: proposedAction,
                path: 'items.*.issues.*.actions.*',
                paths: DRAFT_PATHS,
                omit: OMIT
            }),
            _.identity
        );
        return { action, draft: true };
    }
};

const compileIssue = ({ proposedIssue, currentIssue }) => {
    if (currentIssue && !currentIssue.draft) {
        const {
            id,
            goalId,
            issueId,
            questionnaireResponseId,
            obsolete,
            details: currentDetails,
            relations: currentRelations,
            comment: currentComment,
            diff: currentDiff
        } = _.cloneDeep(currentIssue);
        const {
            details: proposedDetails,
            relations: proposedRelations,
            comment: proposedComment
        } = _.cloneDeep(proposedIssue);
        const { obtained: details, diff: detailsDiff } = compileKeys({
            current: currentDetails,
            proposed: proposedDetails,
            initial: {},
            currentDiff
        });
        const { obtained: relations, diff } = compileKeys({
            current: currentRelations,
            proposed: proposedRelations,
            initial: { diff: detailsDiff },
            currentDiff
        });
        const initial = {
            id,
            goalId,
            issueId,
            questionnaireResponseId,
            obsolete,
            details,
            relations,
            diff
        };
        const issue = compileProperties({
            properties: PROPERTIES.ISSUE,
            before: currentIssue,
            after: proposedIssue,
            initial
        });
        const { rows: comment, draft: draftComment } = compileRows({
            current: currentComment,
            proposed: proposedComment,
            compileRow: compileComment
        });
        issue.comment = !_.isEmpty(comment) && comment;
        const compiled = {
            issue,
            draft: !_.isEmpty(issue.diff) || draftComment
        };
        const currentActions =
            currentIssue?.actions?.reduce((acc, currentAction) => {
                acc[currentAction.id] = currentAction;
                return acc;
            }, {}) || {};
        const actions = [];
        const proposedActions = {};
        proposedIssue?.actions?.forEach((proposedAction) => {
            const currentAction = currentActions[proposedAction.id];
            const { action, draft } = compileAction({
                proposedAction,
                currentAction
            });
            if (draft) {
                compiled.draft = true;
            }
            actions.push(action);
            proposedActions[proposedAction.id] = true;
        });
        currentIssue?.actions?.forEach((currentAction) => {
            if (!currentAction.draft && !proposedActions[currentAction.id]) {
                const action = _.cloneDeep(currentAction);
                actions.push(action);
            }
        });
        compiled.issue.actions = !_.isEmpty(actions) && actions;
        return {
            issue: _.pickBy(compiled.issue, _.identity),
            draft: compiled.draft
        };
    } else {
        const issue = _.pickBy(
            draftify({
                object: proposedIssue,
                path: 'items.*.issues.*',
                paths: DRAFT_PATHS,
                omit: OMIT
            }),
            _.identity
        );
        return { issue, draft: true };
    }
};

const compileItem = ({
    proposedItem,
    proposedIndex,
    currentItem,
    currentIndex
}) => {
    if (currentItem && !currentItem.draft) {
        const conditions =
            currentItem.type === 'other' && currentItem.conditions;
        const { id, details, relations } = _.cloneDeep(proposedItem);
        const initial = { id, details, relations, conditions };
        const item = compileProperties({
            properties: PROPERTIES.ITEM,
            before: currentItem,
            after: proposedItem,
            initial
        });
        if (currentIndex !== proposedIndex) {
            item.diff = item.diff || {};
            item.diff.index = {
                current: proposedIndex,
                previous: currentIndex
            };
        }
        const compiled = { item, draft: !_.isEmpty(item.diff) };
        const currentIssues =
            currentItem?.issues?.reduce((acc, currentIssue) => {
                acc[currentIssue.id] = currentIssue;
                return acc;
            }, {}) || {};
        const issues = [];
        if (item.type !== 'other' || !item.hidden) {
            const proposedIssues = {};
            proposedItem?.issues?.forEach((proposedIssue) => {
                const currentIssue = currentIssues[proposedIssue.id];
                if (currentIssue || currentItem.type !== 'other') {
                    const { issue, draft } = compileIssue({
                        proposedIssue,
                        currentIssue
                    });
                    if (draft) {
                        compiled.draft = true;
                    }
                    issues.push(issue);
                }
                proposedIssues[proposedIssue.id] = true;
            });
            currentItem?.issues?.forEach((currentIssue) => {
                if (!currentIssue.draft && !proposedIssues[currentIssue.id]) {
                    const issue = _.cloneDeep(currentIssue);
                    issues.push(issue);
                }
            });
        }
        compiled.item.issues = !_.isEmpty(issues) && issues;
        return {
            item: _.pickBy(compiled.item, _.identity),
            draft: compiled.draft
        };
    } else {
        const item = _.pickBy(
            draftify({
                object: proposedItem,
                path: 'items.*',
                paths: DRAFT_PATHS,
                omit: OMIT
            }),
            _.identity
        );
        return { item, draft: true };
    }
};

const compilePage = ({ currentPage, proposedPage }) => {
    const { user, metadata, read } = currentPage;
    const initial = {
        user,
        metadata,
        read
    };
    const removed = currentPage?.diff?.items || [];
    const page = compileProperties({
        properties: PROPERTIES.PAGE,
        before: currentPage,
        after: proposedPage,
        initial
    });
    const currentItems =
        currentPage?.items?.reduce((acc, currentItem, index) => {
            const currentIndex = _.has(currentItem, 'diff.index')
                ? currentItem.diff.index.previous
                : index;
            acc[currentItem.id] = { currentItem, currentIndex };
            return acc;
        }, {}) || {};
    const items = [];
    const proposedItems = {};
    proposedPage?.items?.forEach((proposedItem, proposedIndex) => {
        const { currentItem, currentIndex } =
            currentItems[proposedItem.id] || {};
        const { item, draft } = compileItem({
            proposedItem,
            proposedIndex,
            currentItem,
            currentIndex
        });
        if (draft) {
            page.draft = true;
        }
        items.push(item);
        proposedItems[proposedItem.id] = true;
    });
    currentPage?.items?.forEach((currentItem) => {
        if (!currentItem.draft && !proposedItems[currentItem.id]) {
            if (TYPES.DRAFT.includes(currentItem.type)) {
                removed.push(currentItem.id);
                page.draft = true;
            }
            if (TYPES.KEEP.includes(currentItem.type)) {
                const item = _.cloneDeep(currentItem);
                items.push(item);
            }
        }
    });
    if (removed.length > 0) {
        const diff = page.diff || {};
        diff.items = _.uniq(removed);
        page.diff = diff;
    }
    if (!_.isEmpty(page.diff)) {
        page.draft = true;
    }
    page.items = !_.isEmpty(items) && items;
    return _.pickBy(page, _.identity);
};

const unreadComment = ({ comment, read, user }) => {
    const { id } = user || {};
    return (
        comment?.reduce(
            (acc, c) => {
                if ((id && id === c.author?.id) || read[c.id]) {
                    acc.comment.push(c);
                } else {
                    acc.comment.push(_.assign(c, { unread: true }));
                    acc.unread += 1;
                }
                return acc;
            },
            { unread: 0, comment: [] }
        ) || {}
    );
};

const unreadPage = ({
    page,
    canReadClinicalData = true,
    clinicalUsers = []
}) => {
    const read = page.read?.reduce((acc, id) => {
        acc[id] = true;
        return acc;
    }, {});

    if (read) {
        const { user } = page;
        const total = { unread: 0 };
        const items = page.items?.map((item) => {
            const filteredIssues = canReadClinicalData
                ? item.issues
                : (item.issues || []).filter((issue) => {
                      const authorId = issue.details?.find(
                          (d) => d.key === 'assignee'
                      )?.id;
                      if (!authorId) {
                          return true;
                      }
                      return !clinicalUsers.includes(authorId);
                  });
            const issues = filteredIssues?.map((issue) => {
                let actions = issue.actions ?? [];

                if (!canReadClinicalData) {
                    actions = actions.filter((action) => {
                        const authorId = action.details?.find(
                            (d) => d.key === 'assignee'
                        )?.id;
                        if (!authorId) {
                            return true;
                        }
                        return !clinicalUsers.includes(authorId);
                    });
                }

                actions = actions.map((action) => {
                    const { unread, comment } = unreadComment({
                        comment: action.comment,
                        read,
                        user
                    });
                    total.unread += unread;
                    return _.pickBy(
                        _.assign(action, { unread, comment }),
                        _.identity
                    );
                });
                const { unread, comment } = unreadComment({
                    comment: issue.comment,
                    read,
                    user
                });
                total.unread += unread;
                return _.pickBy(
                    _.assign(issue, { actions, unread, comment }),
                    _.identity
                );
            });
            return _.pickBy(_.assign(item, { issues }), _.identity);
        });
        return _.pickBy(
            _.assign(page, { items, unread: total.unread }),
            _.identity
        );
    }
    return page;
};

const compile = ({
    current,
    proposed,
    canReadClinicalData = true,
    clinicalUsers = []
}) => {
    const data = _.mapValues(current.data, (currentPage, path) => {
        const proposedPage = proposed?.data?.[path];
        const page =
            (proposedPage &&
                compilePage({
                    currentPage,
                    proposedPage
                })) ||
            _.cloneDeep(currentPage);

        const { lists, conflicts } = compileLists(
            {
                currentPage,
                proposedPage: proposedPage || currentPage
            },
            canReadClinicalData,
            clinicalUsers
        );

        if (lists) {
            page.lists = lists;
        }

        if (!_.isEmpty(conflicts)) {
            page.conflicts = conflicts;
        }

        return unreadPage({ page, canReadClinicalData, clinicalUsers });
    });
    return { data };
};

const resolve = ({ compiled, id, time }) => {
    const data = _.mapValues(compiled.data, (compiledPage) => {
        const page = _.cloneDeep(compiledPage);
        const resolved = _.transform(
            page.conflicts || {},
            (acc, issues) =>
                issues.reduce((accc, issue) => {
                    if (issue.cancel) {
                        const actions = issue.actions?.map((action) => {
                            if (action.cancel) {
                                const comment = action.comment || [];
                                comment.push({
                                    id: id(),
                                    author: page.user,
                                    createdAt: time(),
                                    value: 'Canceled due to removal from grid screen',
                                    draft: true
                                });
                                return _.omit(_.assign(action, { comment }), [
                                    'cancel'
                                ]);
                            }
                            return action;
                        });
                        const comment = issue.comment || [];
                        comment.push({
                            id: id(),
                            author: page.user,
                            createdAt: time(),
                            value: 'Canceled due to removal from grid screen',
                            draft: true
                        });
                        accc[issue.id] = _.omit(
                            _.assign(issue, {
                                comment,
                                actions
                            }),
                            ['cancel']
                        );
                    } else {
                        accc[issue.id] = issue;
                    }
                    return accc;
                }, acc),
            {}
        );
        const items = page.items?.map((item) => {
            const issues = item.issues?.map(
                (issue) => resolved[issue.id] || issue
            );
            return _.pickBy(_.assign(item, { issues }), _.identity);
        });
        return _.omit(
            _.pickBy(_.assign(page, { items, draft: true }), _.identity),
            ['conflicts']
        );
    });
    return { data };
};

module.exports = {
    clean,
    compile,
    describe,
    resolve
};
